import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ApiError, api } from '../lib/api/client';

type Step = 'email' | 'code' | 'done';

/**
 * Public "Delete your account" page — no sign-in, no app. It's the web
 * deletion path Google Play requires (its URL goes in the Play Console) and
 * works for VON News and VON iPlayer accounts alike.
 *
 * Ownership is proved with a 6-digit code emailed to the address. Reader
 * accounts are deleted immediately; staff accounts become a request an
 * administrator completes within 7 days (the account is signed out and
 * can't sign in meanwhile). The API answers the first step the same way
 * whether or not the address has an account.
 */
export default function DeleteAccount(): JSX.Element {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function requestCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post('/account/delete/request', { email: email.trim() }, { anonymous: true });
      setCode('');
      setStep('code');
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDeletion(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const resp = await api.post<{ ok: boolean; pending: boolean }>(
        '/account/delete/confirm',
        { email: email.trim(), code },
        { anonymous: true },
      );
      setPending(resp.pending);
      setStep('done');
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface px-5 py-12">
      <div className="w-full max-w-md">
        <Logo size={36} withWordmark />

        <h1 className="mt-8 font-heading text-[28px] font-semibold leading-tight tracking-tight text-ink">
          Delete your VON account
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">
          For accounts in the VON News and VON iPlayer apps. You can also do this in either
          app: <strong>More → Delete account</strong>.
        </p>

        <div className="mt-5 rounded-md border border-line bg-surface-muted/50 px-4 py-3 text-[13px] leading-relaxed text-ink-muted">
          <p className="font-medium text-ink">What gets deleted</p>
          <p className="mt-1">
            Your account and sign-in, bookmarks, reactions, eyewitness reports with their photos
            and videos, and notification settings. Nothing is kept afterwards. Staff accounts
            (correspondents and editors) are removed by an administrator within 7 days; you’re
            signed out straight away.
          </p>
        </div>

        {error ? (
          <div className="mt-5 flex items-start gap-2.5 rounded-md border border-danger/30 bg-danger/[0.04] px-3 py-2.5 text-[13px] text-danger">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        ) : null}

        {step === 'email' ? (
          <div className="mt-6 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="del-email">Email address on the account</Label>
              <Input
                id="del-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <Button
              className="w-full justify-center"
              onClick={() => void requestCode()}
              disabled={busy || !email.trim()}
            >
              {busy ? 'Sending…' : 'Email me a code'}
            </Button>
          </div>
        ) : null}

        {step === 'code' ? (
          <div className="mt-6 space-y-3">
            <p className="text-[13px] leading-relaxed text-ink-muted">
              If <strong>{email.trim()}</strong> has a VON account, we’ve emailed it a 6-digit code.
              It expires in 15 minutes.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="del-code">Code</Label>
              <Input
                id="del-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
              />
            </div>
            <Button
              className="w-full justify-center bg-danger hover:bg-danger/90"
              onClick={() => void confirmDeletion()}
              disabled={busy || code.length !== 6}
            >
              {busy ? 'Deleting…' : 'Permanently delete my account'}
            </Button>
            <div className="flex items-center justify-between text-[13px]">
              <button
                type="button"
                className="text-brand hover:underline disabled:opacity-50"
                disabled={busy}
                onClick={() => void requestCode()}
              >
                Send a new code
              </button>
              <button
                type="button"
                className="text-brand hover:underline"
                onClick={() => {
                  setStep('email');
                  setError(null);
                }}
              >
                Use a different email
              </button>
            </div>
          </div>
        ) : null}

        {step === 'done' ? (
          <div className="mt-6 flex items-start gap-3 rounded-md border border-brand/30 bg-brand/[0.05] px-4 py-3 text-[14px] leading-relaxed text-ink">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <p>
              {pending
                ? 'Your request has been sent. An administrator will remove your account within 7 days, and you’ve been signed out everywhere.'
                : 'Your account and its data have been deleted. You’ve been signed out everywhere.'}
            </p>
          </div>
        ) : null}
      </div>
    </main>
  );
}

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Too many attempts. Wait a few minutes and try again.';
    if (err.status === 503) return 'Email isn’t available right now, so we can’t send a code. Try again later.';
    return err.message;
  }
  return 'Something went wrong. Check your connection and try again.';
}
